import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "d-flex justify-center align-center",
  style: {"min-height":"60vh"}
}
const _hoisted_2 = {
  key: 2,
  class: "d-flex justify-center align-center",
  style: {"min-height":"60vh"}
}
const _hoisted_3 = { class: "text-center pa-2" }
const _hoisted_4 = { class: "text-caption text-uppercase text-medium-emphasis font-weight-medium mb-1" }
const _hoisted_5 = { class: "text-h6 font-weight-medium" }
const _hoisted_6 = { class: "d-flex justify-space-between" }
const _hoisted_7 = { class: "text-body-2 font-weight-medium" }
const _hoisted_8 = { class: "text-body-2" }
const _hoisted_9 = { class: "text-right" }
const _hoisted_10 = {
  key: 1,
  class: "text-body-2 text-medium-emphasis text-center pa-4"
}
const _hoisted_11 = { class: "log-entries pa-4" }
const _hoisted_12 = {
  key: 1,
  class: "text-body-2 text-medium-emphasis text-center pa-8"
}
const _hoisted_13 = { class: "text-no-wrap" }
const _hoisted_14 = { class: "text-right" }
const _hoisted_15 = {
  key: 1,
  class: "text-body-2 text-medium-emphasis text-center pa-4"
}
const _hoisted_16 = { class: "text-right" }
const _hoisted_17 = {
  key: 1,
  class: "text-body-2 text-medium-emphasis text-center pa-4"
}
const _hoisted_18 = {
  class: "text-truncate",
  style: {"max-width":"200px"}
}
const _hoisted_19 = { class: "text-center" }
const _hoisted_20 = { class: "text-right" }
const _hoisted_21 = {
  key: 1,
  class: "text-body-2 text-medium-emphasis text-center pa-4"
}

import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick, type Ref } from 'vue'
import { useTheme } from 'vuetify'
import axios from 'axios'

// ─── Types ────────────────────────────────────────────────────────────────────

interface LatestReport {
  time_stamp: string
  num_objects: number
  num_roas: number
  num_cas: number
  num_overlap_vrps: number
  num_diff_vrps: number
  num_total_vrps: number
  max_rp_exec_time: number
  crawler_exec_time: number
  num_repos: number
}

interface Inconsistency {
  file_name: string
  log_message: string
  num_impacted_vrps: number
}

interface ErrorMessage {
  message: string
  count: number
}

interface Repository {
  uri: string
  reachable: boolean
  contained_vrps: number
  num_affected_vrps: number
}

interface ReportData {
  latest_report: LatestReport
  ghostbusters_count: number
  num_repos: number
  vrps_by_rp: [string, number][]
  rp_logs_initial: Record<string, string[]>
  rp_logs_counts: Record<string, number>
  reachable_repos_count: number
  unreachable_repos_count: number
  inconsistencies: Inconsistency[]
  error_messages: ErrorMessage[]
  repositories: Repository[]
}

// ─── Constants ────────────────────────────────────────────────────────────────

const BACKEND_URL = '/api'
const INITIAL_LOG_LIMIT = 50

// ─── State ────────────────────────────────────────────────────────────────────


export default /*@__PURE__*/_defineComponent({
  __name: 'NotifyView',
  setup(__props) {

const theme = useTheme()

const loading = ref(true)
const error = ref<string | null>(null)
const report = ref<ReportData | null>(null)


// RP Logs
const activeRpLogTab = ref<string>('')
const loadingAllLogs = ref<string | null>(null)
const allLogsCache = ref<Record<string, string[]>>({})

// Chart
const reachabilityChartCanvas: Ref<HTMLCanvasElement | null> = ref(null)
let chartInstance: any = null

// ─── Computed ─────────────────────────────────────────────────────────────────

const summaryStats = computed(() => {
  if (!report.value) return []
  const r = report.value.latest_report
  return [
    { label: 'Total Objects', value: r.num_objects?.toLocaleString() },
    { label: 'ROAs', value: r.num_roas?.toLocaleString() },
    { label: 'CAs', value: r.num_cas?.toLocaleString() },
    { label: 'Ghostbusters', value: report.value.ghostbusters_count?.toLocaleString() },
    { label: 'Repositories', value: report.value.num_repos?.toLocaleString() },
    { label: 'Max RP Exec Time (s)', value: r.max_rp_exec_time?.toString() },
    { label: 'Crawler Exec Time (s)', value: r.crawler_exec_time?.toString() },
    { label: 'Last Updated', value: r.time_stamp ? formatDate(r.time_stamp) : 'N/A' }
  ]
})

const vrpBreakdown = computed(() => {
  if (!report.value) return []
  const r = report.value.latest_report
  const consistent = r.num_total_vrps - (r.num_overlap_vrps + r.num_diff_vrps)
  return [
    { label: 'Total Unique VRPs', value: r.num_total_vrps?.toLocaleString() ?? 'N/A' },
    { label: 'VRPs not included by any RP', value: consistent >= 0 ? consistent.toLocaleString() : 'N/A' },
    { label: 'VRPs included by all RPs', value: r.num_overlap_vrps?.toLocaleString() ?? 'N/A' },
    { label: 'VRPs included by some RPs', value: r.num_diff_vrps?.toLocaleString() ?? 'N/A' }
  ]
})

const rpLogNames = computed(() => {
  if (!report.value?.rp_logs_initial) return []
  return Object.keys(report.value.rp_logs_initial)
})

// ─── Methods ──────────────────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr)
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    })
  } catch {
    return dateStr
  }
}

function getDisplayedLogs(rpName: string): string[] {
  if (allLogsCache.value[rpName]) {
    return allLogsCache.value[rpName]
  }
  return report.value?.rp_logs_initial?.[rpName]?.slice(0, INITIAL_LOG_LIMIT) ?? []
}

function hasMoreLogs(rpName: string): boolean {
  if (allLogsCache.value[rpName]) return false
  const count = report.value?.rp_logs_counts?.[rpName] ?? 0
  return count > INITIAL_LOG_LIMIT
}

function getLogCount(rpName: string): number {
  return report.value?.rp_logs_counts?.[rpName] ?? 0
}

async function fetchAllLogs(rpName: string) {
  loadingAllLogs.value = rpName
  try {
    const response = await axios.get(`${BACKEND_URL}/get_all_rp_logs/${rpName}/`)
    if (response.data?.logs) {
      allLogsCache.value[rpName] = response.data.logs
    }
  } catch (e) {
    console.error('Failed to fetch all logs for', rpName, e)
  } finally {
    loadingAllLogs.value = null
  }
}

async function fetchReport() {
  loading.value = true
  error.value = null
  try {
    const response = await axios.get(`${BACKEND_URL}/get_latest_report/`)
    report.value = response.data
    // Set default active RP log tab
    if (rpLogNames.value.length && !activeRpLogTab.value) {
      activeRpLogTab.value = rpLogNames.value[0]
    }
    await nextTick()
    renderChart()
  } catch (e: any) {
    error.value = e?.message ?? 'Failed to load report data.'
    console.error('Failed to fetch report:', e)
  } finally {
    loading.value = false
  }
}


function renderChart() {
  if (!reachabilityChartCanvas.value || !report.value) return

  // Dynamically import Chart.js since it's loaded via CDN in the main template
  // but for the Vue SPA we use the npm package
  const Chart = (window as any).Chart
  if (!Chart) {
    console.warn('Chart.js not available')
    return
  }

  if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }

  const isDark = theme.global.current.value.dark
  const textColor = isDark ? '#ecf0f1' : '#333'
  const bgColor = isDark ? '#34495e' : '#ffffff'

  chartInstance = new Chart(reachabilityChartCanvas.value.getContext('2d'), {
    type: 'doughnut',
    data: {
      labels: ['Reachable', 'Unreachable'],
      datasets: [{
        data: [
          report.value.reachable_repos_count ?? 0,
          report.value.unreachable_repos_count ?? 0
        ],
        backgroundColor: [
          isDark ? '#66BB6A' : '#4CAF50',
          isDark ? '#EF5350' : '#F44336'
        ],
        borderColor: bgColor,
        borderWidth: 2
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: textColor,
            font: { size: 12 }
          }
        }
      }
    }
  })
}

// Re-render chart when theme changes
watch(() => theme.global.current.value.dark, () => {
  renderChart()
})

// ─── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(() => {
  document.title = 'RPKI Dashboard | Notify'
  fetchReport()
})

onBeforeUnmount(() => {
  if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }
})

return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_table = _resolveComponent("v-table")!
  const _component_v_tab = _resolveComponent("v-tab")!
  const _component_v_tabs = _resolveComponent("v-tabs")!
  const _component_v_window_item = _resolveComponent("v-window-item")!
  const _component_v_window = _resolveComponent("v-window")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, {
    fluid: "",
    class: "notify-dashboard pa-4 pa-md-6"
  }, {
    default: _withCtx(() => [
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(_component_v_progress_circular, {
              indeterminate: "",
              color: "primary",
              size: "64"
            })
          ]))
        : (error.value)
          ? (_openBlock(), _createBlock(_component_v_alert, {
              key: 1,
              type: "error",
              variant: "tonal",
              class: "mb-6"
            }, {
              append: _withCtx(() => [
                _createVNode(_component_v_btn, {
                  variant: "text",
                  onClick: fetchReport
                }, {
                  default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                    _createTextVNode("Retry", -1)
                  ]))]),
                  _: 1
                })
              ]),
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(error.value) + " ", 1)
              ]),
              _: 1
            }))
          : (!report.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createVNode(_component_v_card, {
                  variant: "tonal",
                  class: "pa-8 text-center",
                  "max-width": "400"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, {
                      size: "64",
                      color: "grey",
                      class: "mb-4"
                    }, {
                      default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                        _createTextVNode("mdi-database-off-outline", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[4] || (_cache[4] = _createElementVNode("h3", { class: "text-h6 mb-2" }, "No Report Data", -1)),
                    _cache[5] || (_cache[5] = _createElementVNode("p", { class: "text-body-2 text-medium-emphasis" }, " No report data available yet. Please run the update command. ", -1))
                  ]),
                  _: 1
                })
              ]))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                _createVNode(_component_v_card, {
                  class: "mb-6",
                  variant: "outlined"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_card_text, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_row, { dense: "" }, {
                          default: _withCtx(() => [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(summaryStats.value, (stat) => {
                              return (_openBlock(), _createBlock(_component_v_col, {
                                key: stat.label,
                                cols: "6",
                                sm: "4",
                                md: "auto",
                                class: "flex-grow-1"
                              }, {
                                default: _withCtx(() => [
                                  _createElementVNode("div", _hoisted_3, [
                                    _createElementVNode("div", _hoisted_4, _toDisplayString(stat.label), 1),
                                    _createElementVNode("div", _hoisted_5, _toDisplayString(stat.value ?? 'N/A'), 1)
                                  ])
                                ]),
                                _: 2
                              }, 1024))
                            }), 128))
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_row, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_col, {
                      cols: "12",
                      lg: "4"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card, {
                          class: "fill-height dashboard-tile",
                          variant: "outlined"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card_title, { class: "d-flex align-center ga-2" }, {
                              default: _withCtx(() => [
                                _cache[8] || (_cache[8] = _createTextVNode(" VRPs Summary ", -1)),
                                _createVNode(_component_v_tooltip, {
                                  location: "bottom",
                                  "max-width": "240"
                                }, {
                                  activator: _withCtx(({ props: tooltipProps }) => [
                                    _createVNode(_component_v_icon, _mergeProps(tooltipProps, {
                                      size: "small",
                                      color: "grey"
                                    }), {
                                      default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                                        _createTextVNode("mdi-help-circle-outline", -1)
                                      ]))]),
                                      _: 1
                                    }, 16)
                                  ]),
                                  default: _withCtx(() => [
                                    _cache[7] || (_cache[7] = _createTextVNode(" Metrics on the VRPs created by the different relying parties. ", -1))
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_v_divider),
                            _createVNode(_component_v_card_text, { class: "tile-scroll-content" }, {
                              default: _withCtx(() => [
                                _cache[10] || (_cache[10] = _createElementVNode("div", { class: "text-subtitle-2 text-medium-emphasis mb-2" }, "Overall Breakdown", -1)),
                                _createVNode(_component_v_list, {
                                  density: "compact",
                                  class: "pa-0 mb-4"
                                }, {
                                  default: _withCtx(() => [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(vrpBreakdown.value, (item) => {
                                      return (_openBlock(), _createBlock(_component_v_list_item, {
                                        key: item.label,
                                        class: "px-0"
                                      }, {
                                        title: _withCtx(() => [
                                          _createElementVNode("div", _hoisted_6, [
                                            _createElementVNode("span", _hoisted_7, _toDisplayString(item.label), 1),
                                            _createElementVNode("span", _hoisted_8, _toDisplayString(item.value), 1)
                                          ])
                                        ]),
                                        _: 2
                                      }, 1024))
                                    }), 128))
                                  ]),
                                  _: 1
                                }),
                                _createVNode(_component_v_divider, { class: "mb-4" }),
                                _cache[11] || (_cache[11] = _createElementVNode("div", { class: "text-subtitle-2 text-medium-emphasis mb-2" }, "VRPs per Relying Party", -1)),
                                (report.value.vrps_by_rp?.length)
                                  ? (_openBlock(), _createBlock(_component_v_table, {
                                      key: 0,
                                      density: "compact"
                                    }, {
                                      default: _withCtx(() => [
                                        _cache[9] || (_cache[9] = _createElementVNode("thead", null, [
                                          _createElementVNode("tr", null, [
                                            _createElementVNode("th", null, "Relying Party"),
                                            _createElementVNode("th", { class: "text-right" }, "VRP Count")
                                          ])
                                        ], -1)),
                                        _createElementVNode("tbody", null, [
                                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(report.value.vrps_by_rp, ([rpName, vrpCount]) => {
                                            return (_openBlock(), _createElementBlock("tr", { key: rpName }, [
                                              _createElementVNode("td", null, _toDisplayString(rpName), 1),
                                              _createElementVNode("td", _hoisted_9, _toDisplayString(vrpCount?.toLocaleString()), 1)
                                            ]))
                                          }), 128))
                                        ])
                                      ]),
                                      _: 1
                                    }))
                                  : (_openBlock(), _createElementBlock("div", _hoisted_10, " No data available. "))
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_col, {
                      cols: "12",
                      lg: "4"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card, {
                          class: "fill-height dashboard-tile",
                          variant: "outlined"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card_title, { class: "d-flex align-center ga-2" }, {
                              default: _withCtx(() => [
                                _cache[14] || (_cache[14] = _createTextVNode(" Relying Party Logs ", -1)),
                                _createVNode(_component_v_tooltip, {
                                  location: "bottom",
                                  "max-width": "240"
                                }, {
                                  activator: _withCtx(({ props: tooltipProps }) => [
                                    _createVNode(_component_v_icon, _mergeProps(tooltipProps, {
                                      size: "small",
                                      color: "grey"
                                    }), {
                                      default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                                        _createTextVNode("mdi-help-circle-outline", -1)
                                      ]))]),
                                      _: 1
                                    }, 16)
                                  ]),
                                  default: _withCtx(() => [
                                    _cache[13] || (_cache[13] = _createTextVNode(" Logs from various relying party software implementations. ", -1))
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_v_divider),
                            _createVNode(_component_v_card_text, { class: "tile-scroll-content pa-0" }, {
                              default: _withCtx(() => [
                                (rpLogNames.value.length)
                                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                      _createVNode(_component_v_tabs, {
                                        modelValue: activeRpLogTab.value,
                                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((activeRpLogTab).value = $event)),
                                        density: "compact",
                                        "show-arrows": "",
                                        class: "px-4 pt-2"
                                      }, {
                                        default: _withCtx(() => [
                                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rpLogNames.value, (rpName) => {
                                            return (_openBlock(), _createBlock(_component_v_tab, {
                                              key: rpName,
                                              value: rpName,
                                              size: "small"
                                            }, {
                                              default: _withCtx(() => [
                                                _createTextVNode(_toDisplayString(rpName), 1)
                                              ]),
                                              _: 2
                                            }, 1032, ["value"]))
                                          }), 128))
                                        ]),
                                        _: 1
                                      }, 8, ["modelValue"]),
                                      _createVNode(_component_v_window, {
                                        modelValue: activeRpLogTab.value,
                                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((activeRpLogTab).value = $event))
                                      }, {
                                        default: _withCtx(() => [
                                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rpLogNames.value, (rpName) => {
                                            return (_openBlock(), _createBlock(_component_v_window_item, {
                                              key: rpName,
                                              value: rpName
                                            }, {
                                              default: _withCtx(() => [
                                                _createElementVNode("div", _hoisted_11, [
                                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getDisplayedLogs(rpName), (log, idx) => {
                                                    return (_openBlock(), _createElementBlock("div", {
                                                      key: idx,
                                                      class: "log-entry"
                                                    }, _toDisplayString(log), 1))
                                                  }), 128)),
                                                  (hasMoreLogs(rpName))
                                                    ? (_openBlock(), _createBlock(_component_v_btn, {
                                                        key: 0,
                                                        variant: "outlined",
                                                        size: "small",
                                                        color: "primary",
                                                        class: "mt-3",
                                                        loading: loadingAllLogs.value === rpName,
                                                        onClick: ($event: any) => (fetchAllLogs(rpName))
                                                      }, {
                                                        default: _withCtx(() => [
                                                          _createTextVNode(" Show complete logs (" + _toDisplayString(getLogCount(rpName)) + " entries) ", 1)
                                                        ]),
                                                        _: 2
                                                      }, 1032, ["loading", "onClick"]))
                                                    : _createCommentVNode("", true)
                                                ])
                                              ]),
                                              _: 2
                                            }, 1032, ["value"]))
                                          }), 128))
                                        ]),
                                        _: 1
                                      }, 8, ["modelValue"])
                                    ], 64))
                                  : (_openBlock(), _createElementBlock("div", _hoisted_12, " No log data available. "))
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_col, {
                      cols: "12",
                      lg: "4"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card, {
                          class: "fill-height dashboard-tile",
                          variant: "outlined"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card_title, { class: "d-flex align-center ga-2" }, {
                              default: _withCtx(() => [
                                _cache[17] || (_cache[17] = _createTextVNode(" Repository Reachability ", -1)),
                                _createVNode(_component_v_tooltip, {
                                  location: "bottom",
                                  "max-width": "240"
                                }, {
                                  activator: _withCtx(({ props: tooltipProps }) => [
                                    _createVNode(_component_v_icon, _mergeProps(tooltipProps, {
                                      size: "small",
                                      color: "grey"
                                    }), {
                                      default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                                        _createTextVNode("mdi-help-circle-outline", -1)
                                      ]))]),
                                      _: 1
                                    }, 16)
                                  ]),
                                  default: _withCtx(() => [
                                    _cache[16] || (_cache[16] = _createTextVNode(" Status of RPKI repositories. ", -1))
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_v_divider),
                            _createVNode(_component_v_card_text, {
                              class: "d-flex align-center justify-center",
                              style: {"min-height":"280px"}
                            }, {
                              default: _withCtx(() => [
                                _createElementVNode("canvas", {
                                  ref_key: "reachabilityChartCanvas",
                                  ref: reachabilityChartCanvas,
                                  style: {"max-width":"280px","max-height":"280px"}
                                }, null, 512)
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_col, {
                      cols: "12",
                      lg: "4"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card, {
                          class: "fill-height dashboard-tile",
                          variant: "outlined"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card_title, { class: "d-flex align-center ga-2" }, {
                              default: _withCtx(() => [
                                _cache[20] || (_cache[20] = _createTextVNode(" Analysis ", -1)),
                                _createVNode(_component_v_tooltip, {
                                  location: "bottom",
                                  "max-width": "240"
                                }, {
                                  activator: _withCtx(({ props: tooltipProps }) => [
                                    _createVNode(_component_v_icon, _mergeProps(tooltipProps, {
                                      size: "small",
                                      color: "grey"
                                    }), {
                                      default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                                        _createTextVNode("mdi-help-circle-outline", -1)
                                      ]))]),
                                      _: 1
                                    }, 16)
                                  ]),
                                  default: _withCtx(() => [
                                    _cache[19] || (_cache[19] = _createTextVNode(" View persistent VRP differences or object inconsistencies. ", -1))
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_v_divider),
                            _createVNode(_component_v_card_text, { class: "tile-scroll-content" }, {
                              default: _withCtx(() => [
                                (report.value.inconsistencies?.length)
                                  ? (_openBlock(), _createBlock(_component_v_table, {
                                      key: 0,
                                      density: "compact"
                                    }, {
                                      default: _withCtx(() => [
                                        _cache[21] || (_cache[21] = _createElementVNode("thead", null, [
                                          _createElementVNode("tr", null, [
                                            _createElementVNode("th", null, "File Name"),
                                            _createElementVNode("th", null, "Log Message"),
                                            _createElementVNode("th", { class: "text-right" }, "Impacted VRPs")
                                          ])
                                        ], -1)),
                                        _createElementVNode("tbody", null, [
                                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(report.value.inconsistencies, (item, idx) => {
                                            return (_openBlock(), _createElementBlock("tr", { key: idx }, [
                                              _createElementVNode("td", _hoisted_13, _toDisplayString(item.file_name), 1),
                                              _createElementVNode("td", null, _toDisplayString(item.log_message), 1),
                                              _createElementVNode("td", _hoisted_14, _toDisplayString(item.num_impacted_vrps), 1)
                                            ]))
                                          }), 128))
                                        ])
                                      ]),
                                      _: 1
                                    }))
                                  : (_openBlock(), _createElementBlock("div", _hoisted_15, " No inconsistencies found. "))
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_col, {
                      cols: "12",
                      lg: "4"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card, {
                          class: "fill-height dashboard-tile",
                          variant: "outlined"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card_title, { class: "d-flex align-center ga-2" }, {
                              default: _withCtx(() => [
                                _cache[24] || (_cache[24] = _createTextVNode(" Common Errors ", -1)),
                                _createVNode(_component_v_tooltip, {
                                  location: "bottom",
                                  "max-width": "240"
                                }, {
                                  activator: _withCtx(({ props: tooltipProps }) => [
                                    _createVNode(_component_v_icon, _mergeProps(tooltipProps, {
                                      size: "small",
                                      color: "grey"
                                    }), {
                                      default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                                        _createTextVNode("mdi-help-circle-outline", -1)
                                      ]))]),
                                      _: 1
                                    }, 16)
                                  ]),
                                  default: _withCtx(() => [
                                    _cache[23] || (_cache[23] = _createTextVNode(" Most frequent errors aggregated from relying parties. ", -1))
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_v_divider),
                            _createVNode(_component_v_card_text, { class: "tile-scroll-content" }, {
                              default: _withCtx(() => [
                                (report.value.error_messages?.length)
                                  ? (_openBlock(), _createBlock(_component_v_table, {
                                      key: 0,
                                      density: "compact"
                                    }, {
                                      default: _withCtx(() => [
                                        _cache[25] || (_cache[25] = _createElementVNode("thead", null, [
                                          _createElementVNode("tr", null, [
                                            _createElementVNode("th", null, "Message"),
                                            _createElementVNode("th", { class: "text-right" }, "Count")
                                          ])
                                        ], -1)),
                                        _createElementVNode("tbody", null, [
                                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(report.value.error_messages, (err, idx) => {
                                            return (_openBlock(), _createElementBlock("tr", { key: idx }, [
                                              _createElementVNode("td", null, _toDisplayString(err.message), 1),
                                              _createElementVNode("td", _hoisted_16, _toDisplayString(err.count), 1)
                                            ]))
                                          }), 128))
                                        ])
                                      ]),
                                      _: 1
                                    }))
                                  : (_openBlock(), _createElementBlock("div", _hoisted_17, " No common errors found. "))
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_col, {
                      cols: "12",
                      lg: "4"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card, {
                          class: "fill-height dashboard-tile",
                          variant: "outlined"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card_title, { class: "d-flex align-center ga-2" }, {
                              default: _withCtx(() => [
                                _cache[28] || (_cache[28] = _createTextVNode(" Repository Status Details ", -1)),
                                _createVNode(_component_v_tooltip, {
                                  location: "bottom",
                                  "max-width": "240"
                                }, {
                                  activator: _withCtx(({ props: tooltipProps }) => [
                                    _createVNode(_component_v_icon, _mergeProps(tooltipProps, {
                                      size: "small",
                                      color: "grey"
                                    }), {
                                      default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                                        _createTextVNode("mdi-help-circle-outline", -1)
                                      ]))]),
                                      _: 1
                                    }, 16)
                                  ]),
                                  default: _withCtx(() => [
                                    _cache[27] || (_cache[27] = _createTextVNode(" List of repositories, their reachability status, and the number of VRPs they affect. ", -1))
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_v_divider),
                            _createVNode(_component_v_card_text, { class: "tile-scroll-content" }, {
                              default: _withCtx(() => [
                                (report.value.repositories?.length)
                                  ? (_openBlock(), _createBlock(_component_v_table, {
                                      key: 0,
                                      density: "compact"
                                    }, {
                                      default: _withCtx(() => [
                                        _cache[29] || (_cache[29] = _createElementVNode("thead", null, [
                                          _createElementVNode("tr", null, [
                                            _createElementVNode("th", null, "URI"),
                                            _createElementVNode("th", { class: "text-center" }, "Reachable"),
                                            _createElementVNode("th", { class: "text-right" }, "Contained VRPs")
                                          ])
                                        ], -1)),
                                        _createElementVNode("tbody", null, [
                                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(report.value.repositories, (repo, idx) => {
                                            return (_openBlock(), _createElementBlock("tr", { key: idx }, [
                                              _createElementVNode("td", _hoisted_18, _toDisplayString(repo.uri), 1),
                                              _createElementVNode("td", _hoisted_19, [
                                                _createVNode(_component_v_icon, {
                                                  color: repo.reachable ? 'success' : 'error',
                                                  size: "small"
                                                }, {
                                                  default: _withCtx(() => [
                                                    _createTextVNode(_toDisplayString(repo.reachable ? 'mdi-check-circle' : 'mdi-close-circle'), 1)
                                                  ]),
                                                  _: 2
                                                }, 1032, ["color"])
                                              ]),
                                              _createElementVNode("td", _hoisted_20, _toDisplayString(repo.contained_vrps), 1)
                                            ]))
                                          }), 128))
                                        ])
                                      ]),
                                      _: 1
                                    }))
                                  : (_openBlock(), _createElementBlock("div", _hoisted_21, " No repositories found. "))
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ], 64))
    ]),
    _: 1
  }))
}
}

})